import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { getRecordedWorkshopsForCohorts } from '@/lib/db'
import type { UnitWorkshop } from '@/types'
import {
  PlayCircle,
  Video,
  Calendar,
  Clock,
  Loader2
} from 'lucide-react'

/** Convierte una URL de Google Drive (share o directa) a URL de embed */
function toEmbedUrl(url: string): string | null {
  const match = url.match(/drive\.google\.com\/file\/d\/([^/]+)/)
  if (match) return `https://drive.google.com/file/d/${match[1]}/preview`
  if (url.includes('/preview')) return url
  return null
}

export default function RecordedWorkshops() {
  const { student, isInCohort, cohortIds } = useAuth()
  const [workshops, setWorkshops] = useState<UnitWorkshop[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedVideo, setSelectedVideo] = useState<UnitWorkshop | null>(null)

  useEffect(() => {
    if (!student || !isInCohort || cohortIds.length === 0) {
      setLoading(false)
      return
    }

    async function load() {
      setLoading(true)
      try {
        const result = await getRecordedWorkshopsForCohorts(cohortIds)
        if (result.data) setWorkshops(result.data)
      } catch (err) {
        console.error('Error loading recorded workshops:', err)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [student, isInCohort, cohortIds])

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('es-CL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }

  const formatDuration = (seconds: number | null | undefined) => {
    if (!seconds) return null
    const min = Math.floor(seconds / 60)
    const sec = seconds % 60
    return `${min}:${sec.toString().padStart(2, '0')}`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!isInCohort) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <PlayCircle className="w-7 h-7 text-primary" />
            Talleres grabados
          </h1>
        </div>
        <div className="text-center py-12 bg-card rounded-xl border">
          <Video className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium">Sin acceso</p>
          <p className="text-muted-foreground">
            Debes pertenecer a una cohorte activa para ver los talleres grabados
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <PlayCircle className="w-7 h-7 text-primary" />
          Talleres grabados
        </h1>
        <p className="text-muted-foreground mt-1">
          Revisa las grabaciones de los talleres en vivo
        </p>
      </div>

      {/* Lista */}
      {workshops.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border">
          <Video className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium">Sin grabaciones disponibles</p>
          <p className="text-muted-foreground">
            Las grabaciones de los talleres en vivo aparecerán aquí
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {workshops.map((w) => {
            const unitName = (w as any).unit?.title_es || (w as any).unit?.title || ''
            const title = unitName ? `Taller grabado - ${unitName}` : 'Taller grabado'
            const duration = formatDuration((w as any).recording_duration_seconds)

            return (
              <div
                key={w.id}
                className="bg-card rounded-xl border overflow-hidden hover:border-primary/50 transition-colors"
              >
                {/* Video thumbnail area */}
                <div
                  className="relative bg-gray-900 aspect-video flex items-center justify-center cursor-pointer group"
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedVideo(w)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedVideo(w) }}
                >
                  <PlayCircle className="w-16 h-16 text-white/70 group-hover:text-white group-hover:scale-110 transition-all" />
                  {duration && (
                    <span className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/70 text-white text-xs rounded">
                      {duration}
                    </span>
                  )}
                </div>

                {/* Info */}
                <div className="p-4">
                  <h3 className="font-semibold line-clamp-1">{title}</h3>
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
                    {w.scheduled_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(w.scheduled_date)}
                      </span>
                    )}
                    {w.duration_minutes && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {w.duration_minutes} min
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setSelectedVideo(w)}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                  >
                    <PlayCircle className="w-4 h-4" />
                    Ver grabación
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Modal de video */}
      {selectedVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          role="button"
          tabIndex={0}
          aria-label="Cerrar video"
          onClick={() => setSelectedVideo(null)}
          onKeyDown={(e) => { if (e.key === 'Escape') setSelectedVideo(null) }}
        >
          <span className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="bg-black rounded-xl overflow-hidden">
              {selectedVideo.recording_url && toEmbedUrl(selectedVideo.recording_url) ? (
                <iframe
                  src={toEmbedUrl(selectedVideo.recording_url)!}
                  className="w-full aspect-video"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                />
              ) : (
                <div className="w-full aspect-video flex items-center justify-center bg-gray-900 text-gray-400">
                  Video no disponible
                </div>
              )}
              <div className="p-4 bg-gray-900 text-white flex items-center justify-between">
                <h3 className="font-semibold">
                  {(() => { const u = (selectedVideo as any).unit?.title_es || (selectedVideo as any).unit?.title || ''; return u ? `Taller grabado - ${u}` : 'Taller grabado' })()}
                </h3>
              </div>
            </div>
          </span>
        </div>
      )}
    </div>
  )
}
